// FinanceScreen.tsx — MY 하위 자산·성향 도구 (2026-09-07 복기 탭 1단계)
// 투자 자아 카드·자산 구성·자산입력·가계부·목표를 보존한다. 미래가격은 MY 실험실 소속이다.
// 실자산·모의 토글은 홈과 같은 sotda-asset-view 키를 공유한다.
// @ts-nocheck
import '../cs-tokens.css';
import { useNavigate } from 'react-router-dom';
import { useBackTo } from '../../hooks/useBackTo';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';
import { CSAppBar, CSIconBtn, CSIcon } from '../_components/cs';
import { MBTI_TO_CAST } from '../_components/cast';
import { FinanceHeroCard } from './FinanceHeroCard';
import { SkeletonCard } from '../../components/common/Skeleton';
import { useAuth } from '../../lib/useAuth';
import { apiFetch, USE_MOCK } from '../../lib/api';
import { useActiveMacroEvents, pickPrimaryEvent } from '../../hooks/useMacroEvents';
import { useActiveBuyOrNotPolls } from '../../lib/useBuyOrNotPolls';
import { mbtiMock } from '../../lib/mock/mbti';
import { assetMock } from '../../lib/mock/asset';
import type { MbtiPageData } from '../../types/mbti';

// 그림자 단일출처 — 모드별 cs-token을 그대로 상속한다.
const SH1 = 'var(--cs-shadow-1)';
const C = 2 * Math.PI * 54; // 도넛 원주 ≈ 339.29 (r=54)

// 자산 카테고리 도넛 색 순환 (라이트 카드/다크 hero 공통 — 카테고리 수 가변)
// 홈 총자산 막대바와 **같은 팔레트**를 쓴다(2026-08-19 통일). 종전엔 홈=원색 6종 / 금융=이 배열이라
// 같은 카테고리가 화면마다 다른 색이었다. 여기 있던 --cs-fall은 하락(파랑) 의미색이라
// 자산 분류에 쓰면 손익색과 충돌한다 — 그래서 분류 전용 --cs-cat-N으로 교체했다.
const CAT_COLORS_LIGHT = ['var(--cs-cat-1)', 'var(--cs-cat-2)', 'var(--cs-cat-3)', 'var(--cs-cat-4)', 'var(--cs-cat-5)', 'var(--cs-cat-6)'];
// CAT_COLORS_DARK 제거(2026-07-05): 미사용 dead code + 하드코딩 4색(§G 위반).
// 이 배열은 이제 **폴백 전용**이다 — 실제 색 선택은 아래 catColor(키 기반)가 한다.

/* 🔑 색은 **배열 인덱스가 아니라 카테고리 키**로 고른다.
   인덱스로 고르면 데이터 정렬 순서에 색이 끌려가서, 같은 "국내주식"이 홈에서는 진녹인데
   금융탭에서는 연회녹으로 나온다(2026-08-19 실측으로 발견 — 팔레트를 통일하고도 이 버그가 남아 있었다).
   배정은 서버 CATEGORY_META(app/api/mobile/home/route.js)와 1:1로 맞춘다.
   bullAlloc은 키 이름이 다르므로(stock/coin/cash) 의미가 같은 칸에 같은 색을 준다. */
const CAT_COLOR_BY_KEY = {
  cash: 'var(--cs-cat-1)',        // 현금/예적금
  'kr-stock': 'var(--cs-cat-2)',  // 국내주식
  stock: 'var(--cs-cat-2)',       // 모의 배분 '주식' — 국내주식과 같은 축
  foreign: 'var(--cs-cat-3)',     // 해외주식/ETF
  crypto: 'var(--cs-cat-4)',      // 코인
  coin: 'var(--cs-cat-4)',        // 모의 배분 '코인'
  pension: 'var(--cs-cat-5)',     // 연금/IRP
  realty: 'var(--cs-cat-6)',      // 부동산
};
// 맵에 없는 키(향후 카테고리 추가 등)는 순환 팔레트로 폴백 — 색이 비는 것보다 낫다.
const catColor = (key, i) => CAT_COLOR_BY_KEY[key] || CAT_COLORS_LIGHT[i % CAT_COLORS_LIGHT.length];

// ACTIVE 데모 mock 포트폴리오 (모의 배분 주식40/코인30/현금30)
const MOCK_PF = {
  total_value_bull: 1_000_000, holdings_value_bull: 700_000,
  positions: [{ asset_class: 'stock', symbol: 'AAPL', value_bull: 400_000 }, { asset_class: 'crypto', symbol: 'BTC-USD', value_bull: 300_000 }],
};

// 모의 배분 — positions value_bull(주식/코인) + 현금(미투자 BULL)
function computeBullAlloc(pf) {
  if (!pf) return null;
  let stock = 0, crypto = 0;
  for (const p of pf.positions || []) {
    const v = Number(p.value_bull || 0);
    if (p.asset_class === 'crypto' || /-USD$/.test(p.symbol || '')) crypto += v; else stock += v;
  }
  const cash = Math.max(0, Number(pf.total_value_bull || 0) - Number(pf.holdings_value_bull || 0));
  const total = stock + crypto + cash;
  if (total <= 0) return null;
  const pct = (x) => Math.round((x / total) * 100);
  return [{ key: 'stock', label: '주식', pct: pct(stock) }, { key: 'coin', label: '코인', pct: pct(crypto) }, { key: 'cash', label: '현금', pct: pct(cash) }];
}

// 실자산 구성 — asset.categories[].amount 비중
function computeAssetAlloc(asset) {
  const cats = asset?.categories || [];
  const total = cats.reduce((s, c) => s + Number(c.amount || 0), 0);
  if (total <= 0) return null;
  return cats.map((c) => ({ key: c.id, label: c.name, pct: Math.round((Number(c.amount) / total) * 100) }));
}

// 도넛 — slices[].pct(0~100) 순서대로 stroke-dasharray
function Donut({ size, slices, track, center }) {
  let offset = 0; const gap = 2;
  return (
    <svg width={size} height={size} viewBox="0 0 128 128" style={{ flexShrink: 0 }}>
      <g transform="rotate(-90 64 64)">
        <circle cx="64" cy="64" r="54" fill="none" stroke={track} strokeWidth="15" />
        {(slices || []).map((s, i) => {
          const len = Math.max(0, (s.pct / 100) * C - gap);
          const el = <circle key={s.key} cx="64" cy="64" r="54" fill="none" stroke={catColor(s.key, i)} strokeWidth="15" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} />;
          offset += (s.pct / 100) * C;
          return el;
        })}
      </g>
      {center}
    </svg>
  );
}

function SectionTitle({ children, right, mt }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: mt ? '28px 0 12px' : '0 0 12px' }}>
      <h2 style={{ fontFamily: 'var(--cs-font-serif)', fontSize: 20, fontWeight: 500, color: 'var(--cs-ink)', margin: 0, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{children}</h2>
      {right}
    </div>
  );
}

// 진입 카드 (자산 관리·도구) — 틴트 아이콘 박스 + 제목 + 서브 + chevron
function NavCard({ onClick, icon, iconColor, iconBg, title, sub, accent }) {
  return (
    <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--cs-surface)', boxShadow: SH1, borderRadius: 'var(--cs-r-md)', padding: 14, cursor: 'pointer', textAlign: 'left' }}>
      <span style={{ width: 38, height: 38, borderRadius: 'var(--cs-r-sm)', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cs-ink)', letterSpacing: '-0.01em' }}>{title}</div>
        <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--cs-mute)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
      </div>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--cs-mute)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}

// 도구 타일 (2+1 그리드 — §G 무테두리 그림자, 원형 무채 아이콘 배경). CD 금융탭 리디자인 채택.
function ToolTile({ onClick, icon, label, full = false }) {
  return (
    <button onClick={onClick} style={{ gridColumn: full ? '1 / -1' : undefined, border: 0, cursor: 'pointer', background: 'var(--cs-surface)', boxShadow: SH1, borderRadius: 'var(--cs-r-md)', padding: 16, display: 'flex', alignItems: 'center', gap: 12, minHeight: 72, textAlign: 'left' }}>
      <span style={{ width: 40, height: 40, borderRadius: 'var(--cs-r-pill)', background: 'var(--cs-surface-2)', color: 'var(--cs-ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--cs-ink)', letterSpacing: '-0.01em' }}>{label}</span>
    </button>
  );
}

// 활성 이벤트 배너 (거시/살까말까 — 상단 1줄 골드soft로 승격). CD 금융탭 리디자인 채택.
function EventBanner({ onClick, title, desc }) {
  return (
    <button onClick={onClick} style={{ width: '100%', border: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--cs-gold-soft)', borderRadius: 'var(--cs-r-md)', padding: '11px 14px', marginBottom: 16, cursor: 'pointer' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cs-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, color: 'var(--cs-ink)' }}>
        <span style={{ fontWeight: 800 }}>{title}</span>
        <span style={{ color: 'var(--cs-mute)' }}> · {desc}</span>
      </span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cs-mute)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}

// line icons (stroke 1.8, no emoji)
const IconWallet = <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H5a2 2 0 0 0-2 2v-3ZM3 9h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9ZM16 13h2" /></svg>;
const IconBook = <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4ZM5 18a2 2 0 0 0 2 2M9 8h6M9 12h6" /></svg>;
const IconCompass = <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM15.5 8.5l-2 5-5 2 2-5 5-2Z" /></svg>;
const IconTarget = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 12h.01" /></svg>;

export function SotdaFinanceScreen() {
  const navigate = useNavigate();
  const back = useBackTo();
  // 거시 이벤트방(6엔진 Step 4) — 활성 이벤트 있을 때만 NavCard 노출(토글 OFF면 [] → 숨김)
  const macroPrimary = pickPrimaryEvent(useActiveMacroEvents());
  // 살까말까 결정방(6엔진 Step 5) — 활성 poll 있을 때만 노출(토글 OFF면 [] → 숨김)
  const hasBuyOrNot = useActiveBuyOrNotPolls().length > 0;

  // 세션 복원 전(authLoading)엔 mbti 호출 보류 — 미인증 emptyPayload(code="")로 START HERE 깜빡임 방지.
  //   복원 전 = mbti undefined → 스켈레톤, 복원 후 호출 → 완료=HERO / 미진단=START HERE.
  const { loading: authLoading } = useAuth();
  const { data: mbti } = useQuery<MbtiPageData>({
    queryKey: ['mobile', 'mbti'],
    queryFn: () => (USE_MOCK ? Promise.resolve(mbtiMock) : apiFetch<MbtiPageData>('/api/mobile/mbti')),
    staleTime: Infinity,
    enabled: !authLoading,
  });
  const { data: pf } = useQuery({
    queryKey: ['mobile', 'sim', 'portfolio', 'swing'],
    queryFn: () => (USE_MOCK ? Promise.resolve(MOCK_PF) : apiFetch('/api/mobile/sim/portfolio?league=swing')),
    staleTime: 30_000,
  });
  const { data: asset } = useQuery({
    queryKey: ['mobile', 'asset'],
    queryFn: () => (USE_MOCK ? Promise.resolve(assetMock) : apiFetch('/api/mobile/asset')),
    staleTime: 30_000,
  });

  // real/bull 토글 — 홈탭 effView와 동일 Preferences 키 공유. 금융탭(자산 허브) 기본 real.
  const [assetView, setAssetView] = useState('real');
  const [viewPicked, setViewPicked] = useState(false);
  useEffect(() => {
    Preferences.get({ key: 'sotda-asset-view' }).then((r) => { if (r.value) { setAssetView(r.value); setViewPicked(true); } }).catch(() => {});
  }, []);
  const effView = viewPicked ? assetView : 'real';
  const pickView = (v) => {
    const next = v === effView ? (v === 'real' ? 'bull' : 'real') : v;
    setAssetView(next); setViewPicked(true);
    Preferences.set({ key: 'sotda-asset-view', value: next }).catch(() => {});
  };

  const mbtiDone = !!(mbti?.result?.code);
  const r = mbti?.result || {};
  const mbtiCode = (r.code || '').slice(0, 4).toUpperCase();
  const charId = MBTI_TO_CAST[mbtiCode] || 'hwangdoongi'; // MBTI 매칭 캐릭터 (cast.jsx 16종 전부 매핑)
  const realAlloc = computeAssetAlloc(asset);
  const bullAlloc = computeBullAlloc(pf);
  const showReal = effView === 'real';
  const alloc = showReal ? realAlloc : bullAlloc;
  // 범례는 상위 5개만(도넛 과밀 방지)
  const legend = (alloc || []).slice(0, 5);

  // 활성 이벤트 배너 (거시 우선 → 살까말까) — CD 금융탭 리디자인: 하단 NavCard → 상단 1줄 승격
  const eventBanner = macroPrimary
    ? { title: '거시 이벤트 진행 중', desc: '금리 예측 투표 · 모의투자에서 참여', to: '/macro-event' }
    : hasBuyOrNot
      ? { title: '살까말까 투표 진행 중', desc: '내 고민 올리고 AI 디브리핑 받기', to: '/buy-or-not' }
      : null;

  return (
    <div className="cs-root" data-density="regular" data-temp="warm" data-heading="serif"
      style={{ width: '100%', minHeight: '100%', position: 'relative', background: 'var(--cs-bg)' }}>
      <CSAppBar
        title="자산·성향 도구"
        left={<CSIconBtn onClick={back} label="뒤로"><CSIcon name="back" size={22} /></CSIconBtn>}
        right={<>
          <CSIconBtn label="안내" onClick={() => navigate('/about')}><CSIcon name="info" /></CSIconBtn>
        </>}
      />

      <div className="cs-stagger" style={{ padding: '0 16px 24px' }}>
        {/* ── 활성 이벤트 배너 (거시/살까말까 상단 승격 — CD 금융탭 리디자인) ── */}
        {eventBanner && (
          <div style={{ paddingTop: 4 }}>
            <EventBanner onClick={() => navigate(eventBanner.to)} title={eventBanner.title} desc={eventBanner.desc} />
          </div>
        )}

        {/* ── HERO: 투자 자아 카드 (MBTI 정체성 + 매칭 캐릭터 + AI 코칭). 가챠 L카드 보유 시 홀로/프리즘 이펙트 — 후속 연동 ── */}
        {/* 로딩 가드 — mbti 미로드 시 START HERE↔HERO 깜빡임(덮어쓰기 느낌) 방지 */}
        {mbti === undefined ? (
          /* 모의투자식 스켈레톤 — 빈칸 대신 '갱신 중' 느낌 */
          <div style={{ margin: '8px 0 28px' }}><SkeletonCard lines={3} /></div>
        ) : mbtiDone ? (
          <FinanceHeroCard mbti={mbti} charId={charId} mbtiCode={mbtiCode} onClick={() => navigate('/mbti')} />
        ) : (
          /* 미진단 — 진단 유도(START HERE) */
          <div style={{ background: 'var(--cs-gold-soft)', boxShadow: 'var(--cs-shadow-2)', borderRadius: 'var(--cs-r-lg)', padding: '24px 22px', margin: '8px 0 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--cs-gold)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /></svg>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--cs-gold)' }}>START HERE</span>
            </div>
            <h2 style={{ fontFamily: 'var(--cs-font-serif)', fontSize: 26, fontWeight: 800, color: 'var(--cs-ink)', lineHeight: 1.3, margin: '0 0 10px' }}>나는 어떤<br />투자자일까요?</h2>
            <p style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--cs-ink-2)', lineHeight: 1.55, margin: '0 0 20px' }}>3분 진단으로 투자 성향과 어울리는 캐릭터, 맞춤 자산 배분을 알려드려요.</p>
            <button onClick={() => navigate('/mbti/diagnose')} style={{ width: '100%', height: 52, border: 'none', borderRadius: 'var(--cs-r-pill)', background: 'var(--cs-gold)', color: 'var(--cs-accent-ink)', fontWeight: 700, fontSize: 15.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              투자 성향 진단 시작
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>
            </button>
          </div>
        )}

        {/* ── 자산 구성 (실자산↔모의 토글 도넛) ── */}
        <SectionTitle right={
          <div style={{ display: 'inline-flex', background: 'var(--cs-surface-2)', borderRadius: 999, padding: 2 }}>
            {[{ k: 'real', label: '실자산' }, { k: 'bull', label: '모의' }].map((o) => (
              <button key={o.k} onClick={() => pickView(o.k)} style={{ border: 0, cursor: 'pointer', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, background: effView === o.k ? 'var(--cs-surface)' : 'transparent', color: effView === o.k ? (o.k === 'bull' ? 'var(--cs-gold)' : 'var(--cs-ink)') : 'var(--cs-mute)', boxShadow: effView === o.k ? SH1 : 'none' }}>{o.label}</button>
            ))}
          </div>
        }>자산 구성</SectionTitle>
        {alloc ? (
          <div style={{ background: 'var(--cs-surface)', boxShadow: SH1, borderRadius: 'var(--cs-r-md)', padding: 18, marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <Donut size={92} slices={alloc} track="var(--cs-surface-2)" />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {legend.map((s, i) => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: catColor(s.key, i), flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--cs-ink)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--cs-ink)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--cs-surface-2)', borderRadius: 'var(--cs-r-md)', padding: '24px 18px', marginBottom: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cs-ink-2)', marginBottom: 10 }}>{showReal ? '아직 입력한 자산이 없어요' : '모의투자를 시작해보세요'}</div>
            <button onClick={() => navigate(showReal ? '/asset' : '/sim')} style={{ border: 0, background: 'var(--cs-surface)', boxShadow: 'var(--cs-shadow-1)', color: 'var(--cs-ink-2)', borderRadius: 999, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{showReal ? '자산 입력하기' : '모의투자 가기'}</button>
          </div>
        )}

        {/* ── 도구 (2+1 배치 — 기존 타일 모양을 유지하고 목표는 두 열을 쓴다) ── */}
        <SectionTitle>도구</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
          <ToolTile onClick={() => navigate('/asset')} icon={IconWallet} label="자산입력" />
          <ToolTile onClick={() => navigate('/budget')} icon={IconBook} label="가계부" />
          <ToolTile onClick={() => navigate('/goals')} icon={IconTarget} label="목표" full />
        </div>

        {/* 개발 빌드 전용(2026-09-03 효신님) — 두 검수실은 라이브에 노출되던 임시 링크였다. import.meta.env.DEV는 vite build에서 false로 접혀 prod 번들에서 사라진다. */}
        {import.meta.env.DEV && (<>
        {/* ⚠ 임시 — 캐릭터 모션 검수실 진입 (HERO 모션 톤·외곽선·크기 결정 후 제거) */}
        <div style={{ marginBottom: 12 }}>
          <NavCard onClick={() => navigate('/motion-lab')} icon={<CSIcon name="signal" size={19} />} iconColor="var(--cs-accent)" iconBg="var(--cs-surface-2)" title="모션 검수실 (개발 빌드)" sub="캐릭터별 모션 · 배경·크기·속도 조절" />
        </div>
        {/* ⚠ 임시 — 디자인 기법 검수실 진입 (slop 개편 확산 결정 후 제거) */}
        <div style={{ marginBottom: 12 }}>
          <NavCard onClick={() => navigate('/design-lab')} icon={<CSIcon name="edit" size={19} />} iconColor="var(--cs-accent)" iconBg="var(--cs-surface-2)" title="디자인 기법 검수실 (개발 빌드)" sub="개편 전/후 토글 · 눌러서 작동 확인" />
        </div>
        </>)}
        {/* 거시·살까말까 = 활성 시 상단 이벤트 배너로 승격(위 EventBanner). 미래가격 = MY 실험실 소속. (CD 금융탭 리디자인) */}

        {/* ⚠ '투자 스타일(시그니처)' 섹션 — 정적 14종 전체 숨김 (Step 0, 2026-06-18).
            14종→6엔진 재설계 (design doc 2026-06-18-signature-redesign-community-engine-design.md).
            투자 정체성(MBTI)은 이미 HERO 자아 카드로 흡수됨. 나머지 5엔진은 Step 1~6에서 새 위치에 배선. */}

        {/* footer note */}
        <div style={{ marginTop: 18, padding: '0 4px', fontSize: 11.5, color: 'var(--cs-mute)', lineHeight: 1.5, textAlign: 'center' }}>
          회원 계정의 자산·거래내역은 서비스 제공을 위해 서버에서 처리·저장됩니다. AI 분석은 별도 안내와 동의에 따라 제공됩니다. 자세한 내용은 개인정보처리방침에서 확인하세요.
        </div>
      </div>
    </div>
  );
}
